"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/PlanOverviewModal/PlanOverviewModalContent";

import type { FC } from "react";
import { useState } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { PLAN_COPY } from "../onboardingWizardCopy";
import { PLAN_MOVES } from "../quizStepsConfig";
import { ONBOARDING_WIZARD_ASSETS } from "../onboardingWizardAssets";
import { OnboardingEmphasisCtaButton } from "../shared/OnboardingEmphasisCtaButton";
import { PlanMoveRow } from "./PlanMoveRow";
import { PlanFitnessImpactStrip } from "./PlanFitnessImpactStrip";
import type { PlanOverviewModalContentProps } from "./types";
import { AppImage } from "@/src/components/NextImage";

const I18n = I18nUtil.createScoped(Translations);
const GOAL_CARD_TEXT_MAX_LENGTH = 88;

const trimToWordBoundary = (value: string, maxLength: number) => {
  if (value.length <= maxLength) {
    return value;
  }

  const truncated = value.slice(0, maxLength).trimEnd();
  const lastSpaceIndex = truncated.lastIndexOf(" ");

  if (lastSpaceIndex <= 0) {
    return `${truncated}…`;
  }

  return `${truncated.slice(0, lastSpaceIndex)}…`;
};

const getFirstSentence = (value: string) => {
  const firstSentenceMatch = value.match(/^.+?[.!?](?=\s|$)/);

  return firstSentenceMatch?.[0] ?? value;
};

const getGoalCardText = (
  planPreview: PlanOverviewModalContentProps["planPreview"],
) => {
  const title = planPreview?.title?.trim();
  const summary = planPreview?.summary?.trim();
  const sourceText = title || (summary ? getFirstSentence(summary).trim() : "");

  return sourceText
    ? trimToWordBoundary(sourceText, GOAL_CARD_TEXT_MAX_LENGTH)
    : PLAN_COPY.sampleGoal;
};

/**
 * UI10 Plan overview（Figma 96138:32122 H5 · 96344:111001 PC, D20 Modal content）。
 */
export const PlanOverviewModalContent: FC<PlanOverviewModalContentProps> = ({
  isPC = false,
  planPreview,
  planPreviewLoading = false,
  onBack,
  onUnlock,
}) => {
  const [goalBgMissing, setGoalBgMissing] = useState(false);

  const moveCount = planPreview?.planItems?.length;
  const planSubtitle =
    moveCount != null && moveCount > 0
      ? I18n.t("%{var0} moves get you there sooner", {
          var0: moveCount,
        })
      : PLAN_COPY.planSectionSubtitle;
  /** Goal 卡：优先使用 API title；summary 仅作为 fallback 并约束为易扫读的一句话。 */
  const goalCardText = getGoalCardText(planPreview);

  const moves =
    planPreview?.planItems?.map((item, index) => ({
      number: index + 1,
      title: item.title,
      tag: "",
      body: item.description,
      locked: !item.visible,
    })) ?? PLAN_MOVES;

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col bg-background_solid_neutral_secondary",
        isPC ? "min-h-dvh w-full" : "h-full md:mx-auto",
      )}
      data-testid="onboarding-plan-overview-modal"
      data-figma-node="96138:32122"
      data-figma-node-pc="96344:111001"
      data-plan-preview-loading={planPreviewLoading ? "true" : undefined}
    >
      <header
        className={cn(
          "flex shrink-0 items-center pb-3",
          // PC: Figma 96344:111002 — back aligns to 460px column left edge (top 64px)
          isPC ? "mx-auto w-full max-w-[460px] px-0 pt-16" : "px-4 pt-safe",
        )}
      >
        <button
          type="button"
          data-testid="plan-overview-back"
          aria-label="Back"
          onClick={onBack}
          className="flex size-10 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_0px_32px_0px_rgba(0,0,0,0.08)]"
        >
          <Icon
            name="left-arrow-alt"
            size={20}
            className="text-content_primary"
          />
        </button>
      </header>

      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto overscroll-contain no-scrollbar pb-4 pt-4",
          // PC: Figma 96344:111004 — content column 460px
          isPC ? "mx-auto w-full max-w-[460px] px-0" : "px-6",
        )}
        data-testid="plan-main-scroll"
      >
        <section
          className="flex flex-col gap-3"
          data-testid="plan-goal-section"
        >
          <h2
            className="headline_regular_s text-content_primary"
            data-testid="plan-goal-section-title"
          >
            {PLAN_COPY.goalSectionTitle}
          </h2>
          <div
            className="relative overflow-hidden rounded-xl border border-border_accent5_transparent50 bg-background_solid_neutral_primary p-5"
            data-testid="plan-goal-card"
          >
            {!goalBgMissing ? (
              <div
                className="pointer-events-none absolute inset-0 overflow-hidden"
                aria-hidden
              >
                <AppImage
                  src={ONBOARDING_WIZARD_ASSETS.planGoalBg}
                  alt=""
                  width={460}
                  height={206}
                  className="size-full max-w-none object-cover"
                  data-testid="plan-goal-bg"
                  loading="eager"
                  decoding="async"
                  onError={() => setGoalBgMissing(true)}
                />
              </div>
            ) : (
              <div
                className="pointer-events-none absolute inset-0 bg-content_brand_primary"
                aria-hidden
              />
            )}
            <div
              className="relative flex flex-col gap-5"
              data-testid="plan-goal-card-inner"
            >
              <Icon
                name="goal"
                size={24}
                className="text-content_inverse_primary"
              />
              <p
                className="title_xl text-content_inverse_primary"
                data-testid="plan-goal-card-text"
              >
                {goalCardText}
              </p>
            </div>
          </div>
        </section>

        <PlanFitnessImpactStrip impact={planPreview?.impact} />

        <section
          className="flex flex-col gap-6"
          data-testid="plan-plan-section"
        >
          <div data-testid="plan-section-header">
            <h2
              className="headline_regular_s text-content_primary"
              data-testid="plan-section-title"
            >
              {PLAN_COPY.planSectionTitle}
            </h2>
            <p
              className="body_m text-content_tertiary"
              data-testid="plan-section-subtitle"
            >
              {planSubtitle}
            </p>
          </div>

          <div className="flex flex-col gap-3" data-testid="plan-moves-list">
            {moves.map((move) => (
              <PlanMoveRow key={move.number} {...move} />
            ))}
          </div>
        </section>
      </div>

      <div
        className={cn(
          "shrink-0 pt-4",
          // PC: Figma 96344:111057 — CTA column 460px, same left edge as content
          isPC
            ? "mx-auto w-full max-w-[460px] px-0 pb-4"
            : "px-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]",
        )}
        data-testid="plan-footer"
      >
        <div
          className="flex flex-col items-center gap-4"
          data-testid="plan-footer-actions"
        >
          <OnboardingEmphasisCtaButton
            testId="plan-unlock-button"
            label={PLAN_COPY.unlockCta}
            disabled={planPreviewLoading}
            loading={planPreviewLoading}
            sizeClassName="button_l"
            onClick={onUnlock}
          />
        </div>
      </div>
    </div>
  );
};
